import {Injectable} from '@angular/core';
import {MessageErrorDetails} from '../entities/message-error-details.model';
import {AttachmentUploadViewModel} from '../view-models/attachment-upload-view.model';

@Injectable({
  providedIn: 'root'
})
export class AttachmentValidationService {

  static readonly maxUploadSize = 10485760;  //  10MB
  static readonly minFileSize = 1;
  static readonly maxFileCount = 9;
  static readonly fileTypeWhiteList: string[] = ['.txt', '.pdf', '.jpg', '.png', '.docx', '.xlsx', '.pptx'];
  private readonly maxFileNameLength = 260;
  private readonly invalidFileNameCharacters = String.raw`[<^>\\":/|?*\']`;

  public validateAttachment(file: File): MessageErrorDetails {

    if (!file.name) {
      return this.unsupportedError();
    }

    let valid = true;
    valid = valid && this.validateExtensionPeriods(file);
    valid = valid && this.validateFileType(file);
    valid = valid && this.validateAttachmentNameLength(file);
    valid = valid && this.validateFileNameCharacters(file);
    valid = valid && this.validateFileNotMinimumSize(file);
    if (!valid) {
      return this.unsupportedError();
    }
    return undefined;
  }

  public validateNewAttachmentsList(existingFileList: AttachmentUploadViewModel[], additionalFileList: File[]): MessageErrorDetails {
    const existingFileListSize = existingFileList.reduce((accumulatedSize, currentFile) => accumulatedSize + currentFile.fileSize, 0);
    const additionalFileListSize = additionalFileList.reduce((accumulatedSize, currentFile) => accumulatedSize + currentFile.size, 0);

    if (existingFileList.length + additionalFileList.length > AttachmentValidationService.maxFileCount) {
      return this.maxNumberOfFilesError();
    }

    if (existingFileListSize + additionalFileListSize > AttachmentValidationService.maxUploadSize) {
      return this.maxUploadSizeError();
    }

    return undefined;
  }

  private validateExtensionPeriods(file: File): boolean {
    return file.name.split('.').length === 2;
  }

  private validateFileType(file: File): boolean {
    const fileExtension = file.name.split('.')[1];
    const lowerCaseFileExtension = fileExtension.toLowerCase();
    return AttachmentValidationService.fileTypeWhiteList.includes(`.${lowerCaseFileExtension}`);
  }

  private validateAttachmentNameLength(file: File): boolean {
    return file.name.split('.')[0].length <= this.maxFileNameLength;
  }

  private validateFileNameCharacters(file: File): boolean {
    return new RegExp(this.invalidFileNameCharacters).exec(file.name.split('.')[0]) == null;
  }

  private validateFileNotMinimumSize(file: File): boolean {
    return file.size >= AttachmentValidationService.minFileSize;
  }

  private unsupportedError(): MessageErrorDetails {
    return {
      errorMessage: 'Unsupported file.',
      errorDescription: 'Please select a different file.'
    };
  }

  private maxUploadSizeError(): MessageErrorDetails {
    return {
      errorMessage: 'Upload exceeds maximum file size.',
      errorDescription: 'Please ensure upload is less than 10MB.'
    };
  }

  private maxNumberOfFilesError(): MessageErrorDetails {
    return {
      errorMessage: 'Upload exceeds maximum number.',
      errorDescription: 'Please select up to 9 files.'
    };
  }
}
